import React, { useState, useMemo } from "react";
import ReactApexChart from "react-apexcharts";
import { formatCurrency } from "../utils/calculations";
import { getFontFamily } from "../utils/fontConfig";
import { RunwayProjection } from "../types";

interface RunwayChartProps {
  data: RunwayProjection[];
  isSeizedScenario?: boolean;
}

const RunwayChart: React.FC<RunwayChartProps> = ({
  data,
  isSeizedScenario = false,
}) => {
  const [timeframe, setTimeframe] = useState<"6M" | "1Y" | "2Y" | "3Y" | "5Y">(
    "6M"
  );

  const chartData = useMemo(() => {
    if (!data || data.length === 0) {
      return [];
    }

    const monthsToShow =
      timeframe === "6M"
        ? 6
        : timeframe === "1Y"
        ? 12
        : timeframe === "2Y"
        ? 24
        : timeframe === "3Y"
        ? 36
        : 60;
    const filteredData = data.slice(0, monthsToShow).map((item) => ({
      ...item,
      balance: Math.round((item.balance || 0) * 100) / 100,
      runwayMonths: Math.round((item.runwayMonths || 0) * 10) / 10,
    }));

    // Filter out any items with invalid data
    return filteredData.filter(
      (item) =>
        typeof item.balance === "number" &&
        !isNaN(item.balance) &&
        isFinite(item.balance) &&
        typeof item.runwayMonths === "number" &&
        !isNaN(item.runwayMonths) &&
        isFinite(item.runwayMonths)
    );
  }, [data, timeframe]);

  const chartOptions = useMemo(
    () => ({
      chart: {
        type: "area" as const,
        height: 350,
        fontFamily: getFontFamily(),
        toolbar: {
          show: false,
        },
        zoom: {
          enabled: false,
        },
      },
      series: [
        {
          name: "Balance",
          data: chartData.map((item) => item.balance),
        },
      ],
      colors: ["#10b981"],
      stroke: {
        curve: "smooth" as const,
        width: 3,
      },
      fill: {
        type: "gradient",
        gradient: {
          shadeIntensity: 1,
          opacityFrom: 0.3,
          opacityTo: 0,
          stops: [0, 100],
          colorStops: [
            {
              offset: 0,
              color: "#10b981",
              opacity: 0.3,
            },
            {
              offset: 100,
              color: "#10b981",
              opacity: 0,
            },
          ],
        },
      },
      dataLabels: {
        enabled: false,
      },
      grid: {
        borderColor: "var(--chart-grid-color, #e5e7eb)",
        strokeDashArray: 3,
      },
      xaxis: {
        categories: chartData.map((item) => {
          const date = new Date();
          date.setMonth(date.getMonth() + item.month);
          return date.toLocaleDateString("en-US", {
            month: "2-digit",
            year: "2-digit",
          });
        }),
        labels: {
          style: {
            colors: "var(--chart-text-color, #6b7280)",
            fontSize: "12px",
          },
        },
      },
      yaxis: {
        labels: {
          formatter: (value: number) => `$${(value / 1000).toFixed(0)}k`,
          style: {
            colors: "var(--chart-text-color, #6b7280)",
            fontSize: "12px",
          },
        },
      },
      tooltip: {
        theme: "light",
        style: {
          fontSize: "12px",
          fontFamily: getFontFamily(),
        },
        x: {
          show: true,
          formatter: (val: number) =>
            chartData[val] ? chartData[val].month.toString() : val.toString(),
        },
        y: {
          formatter: (value: number) => formatCurrency(value),
        },
        custom: ({ series, seriesIndex, dataPointIndex, w }: any) => {
          const balance = series[seriesIndex][dataPointIndex];
          const runway = chartData[dataPointIndex]?.runwayMonths || 0;
          const month = chartData[dataPointIndex]?.month || 0;

          return `
          <div class="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-3">
            <p class="text-gray-700 dark:text-gray-300 font-medium">Month ${month}</p>
            <p class="text-gray-900 dark:text-white">Balance: ${formatCurrency(
              balance
            )}</p>
            <p class="text-gray-700 dark:text-gray-300">Runway: ${runway.toFixed(
              1
            )} months</p>
          </div>
        `;
        },
      },
      responsive: [
        {
          breakpoint: 640,
          options: {
            chart: {
              type: "line" as const,
              height: 250,
            },
            stroke: {
              width: 4,
            },
            fill: {
              type: "solid",
              opacity: 0,
            },
            markers: {
              size: 6,
              hover: {
                size: 8,
              },
            },
            xaxis: {
              labels: {
                style: {
                  fontSize: "10px",
                },
              },
            },
            yaxis: {
              labels: {
                style: {
                  fontSize: "10px",
                },
              },
            },
          },
        },
      ],
    }),
    [chartData, timeframe]
  );

  // Add error handling for empty or invalid data
  if (!data || data.length === 0) {
    return (
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">Runway Projection</h3>
        </div>
        <div className="p-8 text-center text-gray-500">
          <p>No data available for runway projection</p>
        </div>
      </div>
    );
  }

  return (
    <div className="card hidden sm:block">
      <div className="card-header">
        <h3 className="card-title">Financial Runway Projection</h3>
      </div>

      {/* Scenario Context */}
      <div className="px-4 pb-2">
        <div
          className={`text-sm p-3 rounded-lg ${
            isSeizedScenario
              ? "bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-800 dark:text-red-200"
              : "bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 text-blue-800 dark:text-blue-200"
          }`}>
          <div className="flex items-start space-x-2">
            <div
              className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${
                isSeizedScenario ? "bg-red-500" : "bg-blue-500"
              }`}></div>
            <div>
              <p className="font-medium">
                {isSeizedScenario
                  ? "Income Seized Scenario"
                  : "Normal Scenario"}
              </p>
              <p className="text-xs mt-1 opacity-90">
                {isSeizedScenario
                  ? "Shows how long your current balance would last if you lost all income. This is a conservative worst-case scenario."
                  : "Shows your projected balance with current income and expenses. This assumes your income continues as expected."}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="px-4 pb-4">
        <div className="flex flex-col gap-2">
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Timeframe:
          </span>
          <div className="grid grid-cols-5 bg-gray-100 dark:bg-gray-800 rounded-lg p-1 gap-1 w-full">
            {(["6M", "1Y", "2Y", "3Y", "5Y"] as const).map((period) => (
              <button
                key={period}
                onClick={() => setTimeframe(period)}
                className={`px-3 py-3 text-sm font-medium rounded-md transition-colors flex items-center justify-center min-h-[48px] ${
                  timeframe === period
                    ? "bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm"
                    : "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-200 dark:hover:bg-gray-700"
                }`}>
                {period}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="p-3 sm:p-4">
        {/* Desktop Chart */}
        <div className="hidden lg:block">
          {chartData.length > 0 ? (
            <div className="relative">
              {(() => {
                try {
                  return (
                    <ReactApexChart
                      key={`chart-${timeframe}`}
                      options={chartOptions}
                      series={chartOptions.series}
                      type="area"
                      height={350}
                    />
                  );
                } catch (error) {
                  console.error("Error rendering RunwayChart:", error);
                  return (
                    <div className="text-center text-gray-500 py-8">
                      <p>Error loading chart data</p>
                    </div>
                  );
                }
              })()}
            </div>
          ) : (
            <div className="text-center text-gray-500 py-8">
              <p>No data available for chart</p>
            </div>
          )}
        </div>

        {/* Mobile Data Table */}
        <div className="lg:hidden">
          <div className="space-y-2">
            {chartData.slice(0, 6).map((item, index) => {
              const date = new Date();
              date.setMonth(date.getMonth() + item.month);
              return (
                <div
                  key={index}
                  className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  <div className="flex items-center space-x-2 min-w-0 flex-1">
                    <div className="w-2 h-2 bg-green-500 rounded-full flex-shrink-0"></div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                        Month {item.month}
                      </p>
                      <p className="text-xs text-gray-600 dark:text-gray-400 truncate">
                        {date.toLocaleDateString("en-US", {
                          month: "short",
                          year: "2-digit",
                        })}
                      </p>
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0 ml-2">
                    <p className="text-sm font-semibold text-gray-900 dark:text-white">
                      {formatCurrency(item.balance)}
                    </p>
                    <p className="text-xs text-gray-600 dark:text-gray-400">
                      {item.runwayMonths.toFixed(1)}m
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RunwayChart;
